import React from 'react';
import { useNavigate } from 'react-router-dom';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer';
import { useSalaryProcessList } from './hooks/useSalaryProcessList';
import SalaryProcessTable from './components/SalaryProcessTable';
import SalaryProcessEmptyState from './components/SalaryProcessEmptyState';

// Modals
import UploadSalaryFileModal from './components/UploadSalaryFileModal';
import JournalVoucherModal from './components/JournalVoucherModal';
import SalaryPaymentModal from './components/SalaryPaymentModal';
import BatchPayslipModal from './components/BatchPayslipModal';
import SalarySlipFormatModal from './components/SalarySlipFormatModal';

export default function SalaryProcessListScreen() {
  const { state, actions } = useSalaryProcessList();
  const navigate = useNavigate();

  const handleNavigateToProcess = () => {
    navigate(
      `/payroll/salary-process/process?month=${state.monthFilter}&year=${state.yearFilter}&companyId=${state.companyFilter}&branchId=${state.branchFilter}&deptId=${state.departmentFilter}`
    );
  };

  const statusTabs = [
    { key: 'All', label: 'All' },
    { key: 'Processed', label: 'Processed' },
    { key: 'Paid', label: 'Paid' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Canopy */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant mb-1">
            <span>Payroll</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Payroll Processing</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="text-primary font-black">Salary Process</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Salary Process
          </h1>
          <p className="text-xs text-on-surface-variant font-medium mt-1">
            Process monthly payroll, compute statutory deductions, post journals, and execute salary disbursements.
          </p>
        </div>

        {/* Action Cluster: Status Tabs + Primary Run Process CTA */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.handleStatusTabChange(tab.key)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${state.statusFilter === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Primary CTA */}
          <button
            type="button"
            onClick={handleNavigateToProcess}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition-all select-none cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">payments</span>
            <span>Run Salary Process</span>
          </button>
        </div>
      </section>


      {/* Shared Filters Component */}
      <SharedFilters
        searchText={state.searchText}
        setSearchText={actions.setSearchText}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        employeeFilter={state.employeeFilter}
        groupFilter={state.empGroupFilter}
        genderFilter={state.genderFilter}
        statusFilter={state.statusFilter}
        monthFilter={state.monthFilter}
        yearFilter={state.yearFilter}
        companyOptions={state.companyOptions}
        branchOptions={state.branchOptions}
        departmentOptions={state.departmentOptions}
        groupOptions={state.groupOptions}
        genderOptions={state.genderOptions}
        monthOptions={state.monthOptions}
        yearOptions={state.yearOptions}
        showStatus={false}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setEmployeeFilter={actions.setEmployeeFilter}
        setGroupFilter={actions.setEmpGroupFilter}
        setGenderFilter={actions.setGenderFilter}
        setMonthFilter={actions.setMonthFilter}
        setYearFilter={actions.setYearFilter}
        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}
        visibleAccordionFilters={[
          'company',
          'branch',
          'department',
          'employee',
          'group',
          'gender',
          'monthYear',
        ]}
        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Data Table or Empty State Banner */}
      {state.records.length === 0 && !state.isLoading ? (
        <SalaryProcessEmptyState
          onResetFilters={actions.resetFilters}
          onProcessSalary={handleNavigateToProcess}
        />
      ) : (
        <div className="space-y-4">
          <SalaryProcessTable
            records={state.records}
            isLoading={state.isLoading}
            onOpenPayslip={actions.handleOpenSinglePayslip}
            onRollback={() => actions.handleToolSelect('rollback')}
            onPostJournal={() => actions.setIsJournalModalOpen(true)}
            onProcessPayment={() => actions.setIsPaymentModalOpen(true)}
            onResetFilters={actions.resetFilters}
          />

          {/* Table Footer Control */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-surface-container-lowest px-6 py-4 rounded-2xl border border-outline-variant/20 text-xs font-semibold text-on-surface-variant shadow-xs">
            <span>
              Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
              {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
            </span>

            <AppPagination
              page={state.page}
              totalPages={state.totalPages}
              onPageChange={actions.setPage}
              onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
              onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
              canPrevious={state.page > 1}
              canNext={state.page < state.totalPages}
            />
          </div>
        </div>
      )}

      {/* Predefined Reports Drawer */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsDrawerOpen}
        onClose={() => actions.setIsReportsDrawerOpen(false)}
        title="Salary Process Reports"
        reports={state.predefinedReports}
        onAction={actions.handleReportAction}
      />

      {/* Modals for Tools Menu (Upload, Accounting, Payment, Payslip, Format) */}
      <UploadSalaryFileModal
        isOpen={state.isUploadModalOpen}
        onClose={() => actions.setIsUploadModalOpen(false)}
        onSuccess={actions.refresh}
      />

      <JournalVoucherModal
        isOpen={state.isJournalModalOpen}
        onClose={() => actions.setIsJournalModalOpen(false)}
        month={String(state.monthFilter)}
        year={String(state.yearFilter)}
        totalAmount={state.totalNet}
      />

      <SalaryPaymentModal
        isOpen={state.isPaymentModalOpen}
        onClose={() => actions.setIsPaymentModalOpen(false)}
        month={String(state.monthFilter)}
        year={String(state.yearFilter)}
      />

      <BatchPayslipModal
        isOpen={state.isPayslipModalOpen}
        onClose={() => actions.setIsPayslipModalOpen(false)}
        record={state.selectedRecordForPayslip}
        allRecords={state.records}
        month={String(state.monthFilter)}
        year={String(state.yearFilter)}
      />

      <SalarySlipFormatModal
        isOpen={state.isFormatModalOpen}
        onClose={() => actions.setIsFormatModalOpen(false)}
      />
    </div>
  );
}
